import { ExecutionContext, ASTNode, FunctionCallNode } from '../types';

/**
 * 表达式执行器
 * 负责执行模板中的表达式和函数调用
 */
export class ExpressionExecutor {
  public context: ExecutionContext;

  constructor(context: ExecutionContext) {
    this.context = context;
  }

  // 计算 Levenshtein 距离
  private static levenshtein(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,      // 删除
          dp[i][j - 1] + 1,      // 插入
          dp[i - 1][j - 1] + cost // 替换
        );
      }
    }

    return dp[m][n];
  }

  // 从候选集合中找出相近名称
  private getSimilarNames(input: string, candidates: string[], maxSuggestions: number = 5): string[] {
    const lowerInput = input.toLowerCase();
    const scored = candidates.map(name => {
      const score = ExpressionExecutor.levenshtein(lowerInput, name.toLowerCase());
      return { name, score };
    });

    // 动态阈值：长度较短时更严格
    const threshold = Math.max(1, Math.floor(input.length / 3));
    const filtered = scored
      .filter(s => s.score <= threshold + 1)
      .sort((a, b) => a.score - b.score)
      .slice(0, maxSuggestions)
      .map(s => s.name);

    return filtered;
  }

  /**
   * 执行AST节点
   * @param ast AST节点
   * @returns 执行结果
   */
  executeAST(ast: ASTNode): any {
    switch (ast.type) {
      case 'functionCall':
        return this.executeFunctionCallAST(ast);
      case 'literal':
        return ast.value;
      default:
        throw new Error(`Unknown AST node type: ${(ast as any).type}`);
    }
  }


  /**
   * 执行函数调用（AST版本）
   * @param node 函数调用AST节点
   * @returns 函数执行结果
   */
  private executeFunctionCallAST(node: FunctionCallNode): any {
    const func = this.context.functions[node.name];

    if (!func) {
      // 检查是否是变量
      if (this.context.variables.hasOwnProperty(node.name)) {
        return this.context.variables[node.name];
      }

      // 提供友好的错误信息
      const variableNames = Object.keys(this.context.variables || {});
      const functionNames = Object.keys(this.context.functions);
      const pool = Array.from(new Set([...variableNames, ...functionNames]));
      const suggestions = this.getSimilarNames(node.name, pool);
      const suffix = suggestions.length > 0 ? `\nDid you mean:\n  - ${suggestions.join('\n  - ')}` : '';
      throw new Error(`Unknown function or variable: ${node.name}${suffix}`);
    }

    // 递归执行参数（参数中的函数调用都是嵌套的）
    const args = node.args.map(arg => this.executeAST(arg)).map(arg => arg?.type === 'listContainer' && !arg.elements.length ? undefined : arg);
    const result = func.execute(this.context, ...args);

    return result;
  }
} 